import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Box, FormControl, styled } from "@mui/material";
import type { PatientBaseAttributes } from "@interfaces";
import { DocumentType } from "@interfaces";
import { FormContainer } from "@styles/styled";
import { ErrorMessage, InputLabel, ObscuredSingleInput } from "./styled";
import {
  BirthdateInput,
  BottomActionArea,
  BottomActionButton,
  ExtraWhiteSpace,
  FullScreenLoader,
  Header,
  InputFieldWrapper,
  SingleFormInput,
  SingleFormInputSkeleton,
  scrollToTop,
  NavigateBeforeButton,
  PhoneInput,
  DocumentTypeSelection,
  FormContentContainer,
  FormBody,
  Autocomplete,
  WhiteTextField,
} from "@components";
import { ShareLink } from "./components/ShareLink";
import { useDoctorContext } from "@contexts";
import { useCreatePatient, useCreateRequest, useGetPatientByDocument } from "@hooks";
import {
  capitalizeParametValue,
  getBirthdateDetails,
  handleNumericInputChange,
  replaceCurrentPhoneCode,
  validateWeight,
} from "@utils";
import { redirectToEmptyDocumentFirstStep } from "@utils/redirect";
import { validate, format } from "rut.js";
import regionsWithCommunes from "../../data/regionsWithCommunes.json";
import axios from "axios";
import { getNameParts, isValidPassport } from "@utils";

const Column = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const regions = regionsWithCommunes.map((region) => region.region);

const formInitialState: PatientBaseAttributes = {
  uuid: "",
  rut: null,
  passport: null,
  name: "",
  firstSurname: "",
  secondSurname: "",
  birthdate: "",
  weight: "",
  email: "",
  phone: "",
  region: "Metropolitana de Santiago",
  commune: "",
  street: "",
};

export function AddPatientInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const { documentType, searchedRut = "" } = location.state || {};

  const {
    register,
    handleSubmit,
    getValues,
    control,
    setValue,
    formState: { errors },
    clearErrors,
    trigger,
  } = useForm<PatientBaseAttributes>({ defaultValues: { rut: searchedRut } });

  const doctor = useDoctorContext();
  const [settingPatientData, setSettingPatientData] = useState<boolean>(true);
  const [loadingPatientData, setLoadingPatientData] = useState<boolean>(false);

  const [showAdditionalFields, setShowAdditionalFields] = useState<boolean>(false);

  const [formState, setFormState] = useState(formInitialState);

  // A patient already identified (by RUT or passport) and loaded shows the rest of the
  // fields, adjusted during render when the patient data or its loading change. The
  // previous values start empty so the first render checks too.
  const [prevFormState, setPrevFormState] = useState<PatientBaseAttributes | undefined>(
    undefined,
  );

  const [prevLoadingPatientData, setPrevLoadingPatientData] = useState<
    boolean | undefined
  >(undefined);

  if (formState !== prevFormState || loadingPatientData !== prevLoadingPatientData) {
    setPrevFormState(formState);
    setPrevLoadingPatientData(loadingPatientData);

    if ((formState.rut || formState.passport) && !loadingPatientData) {
      setShowAdditionalFields(true);
    }
  }

  const { mutate: createPatient, isPending } = useCreatePatient();

  const { mutate: createRequest, isPending: isCreatingRequest } = useCreateRequest();

  const { mutateAsync: getPatientByDocument } = useGetPatientByDocument();
  // RUT or Passport:
  const patientInputRut = useWatch({ control, name: "rut" });
  const patientInputPassport = useWatch({ control, name: "passport" });
  const [showRutError, setShowRutError] = useState<boolean>(false);
  const [showPassportError, setShowPassportError] = useState<boolean>(false);

  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType>(
    DocumentType.RUT,
  );

  // Validación de documento
  const hasValidDocument =
    selectedDocumentType === DocumentType.RUT
      ? patientInputRut && validate(patientInputRut)
      : patientInputPassport && isValidPassport(patientInputPassport);

  const isDocumentInputEmpty = !patientInputRut && !patientInputPassport;

  // birthdate:
  const birthdate = useWatch({ control, name: "birthdate" });

  const birthdateDetails = useMemo(() => {
    return getBirthdateDetails(birthdate);
  }, [birthdate]);

  // Regions and communes:
  const selectedRegion = useWatch({ control, name: "region" });
  const selectedCommune = useWatch({ control, name: "commune" });

  // Show only communes for selected region:
  const communes = useMemo(() => {
    const foundRegion = regionsWithCommunes.find((r) => r.region === selectedRegion);

    return foundRegion ? foundRegion.communes.sort() : [];
  }, [selectedRegion]);

  // Phone:
  const [phoneInputRequired, setPhoneInputRequired] = useState<boolean>(false);

  const [currentPhoneCode, setCurrentPhoneCode] = useState({
    name: "Chile",
    icon: "🇨🇱",
    prefix: "+56",
  });

  const weight = useWatch({ control, name: "weight" });

  // Handle back browsing:
  const handleBack = useCallback(() => {
    if (settingPatientData) {
      if (!showAdditionalFields) {
        navigate("/app/nuevo-documento/paciente", { state: { documentType } });
      } else {
        setShowAdditionalFields(false);
      }
    } else {
      setSettingPatientData(true);
    }

    scrollToTop();
  }, [
    settingPatientData,
    setSettingPatientData,
    showAdditionalFields,
    setShowAdditionalFields,
    documentType,
    navigate,
  ]);

  const fetchPatientData = useCallback(async () => {
    setLoadingPatientData(true);
    if (selectedDocumentType === DocumentType.PASSPORT) {
      setShowPassportError(false);
      setShowRutError(false);
      setLoadingPatientData(false);
      setShowAdditionalFields(true);
      setValue("passport", patientInputPassport);
      setValue("rut", null);

      return;
    }

    try {
      const patientRut = patientInputRut ? format(patientInputRut) : null;
      const patient = await getPatientByDocument(patientRut);

      // Automatically fills inputs:
      if (patient) {
        const patientBaseAttributes: PatientBaseAttributes = {
          uuid: patient.uuid,
          rut: patient.rut,
          passport: null,
          name: patient.name,
          firstSurname: patient.firstSurname,
          secondSurname: patient.secondSurname,
          birthdate: patient.birthdate,
          weight: patient.weight,
          email: patient.email,
          phone: patient.phone,
          region: patient.region,
          commune: patient.commune,
          street: patient.street,
        };

        setFormState(patientBaseAttributes);
        setShowRutError(false);
        setShowPassportError(false);
        Object.entries(patientBaseAttributes).forEach(([key, value]) => {
          if (key === "rut" || key === "passport") return;
          setValue(key as keyof PatientBaseAttributes, value);
        });
        if (patient.phone) setPhoneInputRequired(true);
      }
    } catch {
      try {
        const patientRutData = await axios.get(
          `https://api.boostr.cl/rut/name/${patientInputRut}.json`,
        );

        const { firstName, secondName, firstSurname, secondSurname } = getNameParts(
          patientRutData.data.data.name,
        );

        const patientBaseAttributes: PatientBaseAttributes = {
          uuid: "",
          rut: `${patientRutData.data.data.document}-${patientRutData.data.data.dv}`,
          passport: null,
          name: `${firstName} ${secondName}`.trim(),
          firstSurname: firstSurname,
          secondSurname: secondSurname,
          birthdate: "",
          weight: "",
          email: "",
          phone: "",
          region: "",
          commune: "",
          street: "",
        };

        setFormState(patientBaseAttributes);
        setShowRutError(false);
        Object.entries(patientBaseAttributes).forEach(([key, value]) => {
          if (key === "rut") return;
          setValue(key as keyof PatientBaseAttributes, value);
        });
      } catch (boostrError) {
        console.error("Error fetching from api.boostr.cl:", boostrError);
      }
    } finally {
      setLoadingPatientData(false);
      setShowAdditionalFields(true);
    }
  }, [
    patientInputRut,
    patientInputPassport,
    selectedDocumentType,
    setFormState,
    setValue,
    setShowAdditionalFields,
    getPatientByDocument,
  ]);

  const handleDocumentNumberOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormState(formInitialState);
      clearErrors("rut");
      clearErrors("passport");
      if (selectedDocumentType === DocumentType.RUT) {
        setValue("rut", e.target.value);
      } else {
        setValue("passport", e.target.value);
      }

      setShowRutError(false);
      setShowPassportError(false);
      setShowAdditionalFields(false);

      Object.keys(formState).forEach((key) => {
        if (key === "rut" || key === "passport") return;
        setValue(key as keyof PatientBaseAttributes, "");
        clearErrors(key as keyof PatientBaseAttributes);
      });
    },
    [
      formState,
      setFormState,
      setValue,
      setShowRutError,
      setShowPassportError,
      setShowAdditionalFields,
      clearErrors,
      selectedDocumentType,
    ],
  );

  const validateAndSetInputValue = useCallback(
    (value: string, documentType: DocumentType) => {
      if (documentType === DocumentType.RUT) {
        if (validate(value)) {
          setValue("rut", format(value));
          setShowRutError(false);
          clearErrors("rut");

          return true;
        } else {
          setShowRutError(true);

          return false;
        }
      } else {
        if (isValidPassport(value)) {
          setValue("passport", value.toUpperCase());
          setShowPassportError(false);
          clearErrors("passport");

          return true;
        } else {
          setShowPassportError(true);

          return false;
        }
      }
    },
    [setValue, clearErrors, setShowRutError, setShowPassportError],
  );

  const handleDocumentNumberBlur = useCallback(() => {
    if (isDocumentInputEmpty) return;

    setShowRutError(false);
    setShowPassportError(false);
    clearErrors("rut");
    clearErrors("passport");

    validateAndSetInputValue(
      selectedDocumentType === DocumentType.RUT
        ? patientInputRut || ""
        : patientInputPassport || "",
      selectedDocumentType,
    );
  }, [
    patientInputRut,
    patientInputPassport,
    selectedDocumentType,
    validateAndSetInputValue,
    clearErrors,
    setShowRutError,
    setShowPassportError,
    isDocumentInputEmpty,
  ]);

  const handleOnSubmitDocumentNumber = useCallback(() => {
    if (isDocumentInputEmpty) return;

    const isValid = validateAndSetInputValue(
      selectedDocumentType === DocumentType.RUT
        ? patientInputRut || ""
        : patientInputPassport || "",
      selectedDocumentType,
    );

    if (!isValid) return;

    scrollToTop();
    fetchPatientData();
  }, [
    patientInputRut,
    patientInputPassport,
    validateAndSetInputValue,
    fetchPatientData,
    selectedDocumentType,
    isDocumentInputEmpty,
  ]);

  const handleDocumentTypeChange = useCallback(
    (documentType: DocumentType) => {
      setSelectedDocumentType(documentType);
      setValue("rut", "");
      setValue("passport", "");
      setShowRutError(false);
      setShowPassportError(false);
      setShowAdditionalFields(false);
    },
    [setValue],
  );

  const handleDocumentNumberKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;
      if (isDocumentInputEmpty) return;

      // Remove focus from input (to hide keyboard on mobile)
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setShowRutError(false);
      setShowPassportError(false);
      clearErrors("rut");
      clearErrors("passport");

      const isValid = validateAndSetInputValue(
        selectedDocumentType === DocumentType.RUT
          ? patientInputRut || ""
          : patientInputPassport || "",
        selectedDocumentType,
      );

      if (isValid) {
        handleOnSubmitDocumentNumber();
      }
    },
    [
      patientInputRut,
      patientInputPassport,
      validateAndSetInputValue,
      handleOnSubmitDocumentNumber,
      selectedDocumentType,
      clearErrors,
      setShowRutError,
      setShowPassportError,
      isDocumentInputEmpty,
    ],
  );

  // TODO: Use zod to validate form
  const validateForm = useCallback(
    (data: PatientBaseAttributes) => {
      let isValid = true;

      const notRequiredKeys = ["weight", ...(phoneInputRequired ? [] : ["phone"])];

      const hasRut = data.rut?.trim();
      const hasPassport = data.passport?.trim();
      const documentNumberCount = [hasRut, hasPassport].filter(Boolean).length;

      Object.keys(formState).forEach((key) => {
        if (
          key in data &&
          !data[key as keyof PatientBaseAttributes] &&
          !notRequiredKeys.includes(key) &&
          documentNumberCount !== 1
        ) {
          isValid = false;
        }
      });

      return isValid;
    },
    [formState, phoneInputRequired],
  );

  const onPatientDataSubmit = useCallback(
    (formData: PatientBaseAttributes) => {
      setFormState((prevState) => {
        return {
          ...prevState,
          ...formData,
          phone: phoneInputRequired
            ? replaceCurrentPhoneCode(formData.phone, currentPhoneCode.prefix)
            : "",
          region: formData.region !== "" ? formData.region : prevState.region,
        };
      });
      setSettingPatientData(false);
      scrollToTop();
    },
    [currentPhoneCode, phoneInputRequired, setFormState, setSettingPatientData],
  );

  const onSubmit: SubmitHandler<PatientBaseAttributes> = useCallback(
    async (data) => {
      if (!validateForm(data)) return;
      createPatient(
        {
          doctorCode: doctor.code,
          patient: data,
        },
        {
          onSuccess: ({ patientUuid }) => {
            createRequest(
              { patientUuid, documentType },
              {
                onSuccess: (request) => {
                  const { id: requestId, documentType: docType } = request;

                  navigate(redirectToEmptyDocumentFirstStep(docType, requestId));
                },
                onError: () => {
                  navigate("/app/solicitudes");
                },
              },
            );
          },
        },
      );
    },
    [documentType, doctor, validateForm, createPatient, createRequest, navigate],
  );

  // Fill in entries when the formState has already been established, before the fields
  // shown with them are painted.
  useLayoutEffect(() => {
    if ((formState.rut || formState.passport) && !loadingPatientData) {
      Object.entries(formState).forEach(([key, value]) => {
        setValue(key as keyof PatientBaseAttributes, value);
      });
    }
  }, [formState, loadingPatientData, setValue]);

  if (isPending || isCreatingRequest) {
    return (
      <>
        <Header
          title={settingPatientData ? "Datos del paciente" : "Datos de contacto"}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader
              title={isPending ? "Creando paciente..." : "Creando solicitud..."}
            />
          </FormBody>
        </FormContentContainer>
      </>
    );
  }

  return (
    <>
      <Header
        title={settingPatientData ? "Datos del paciente" : "Datos de contacto"}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            {settingPatientData ? (
              <>
                {loadingPatientData ? (
                  <>
                    <SingleFormInputSkeleton />
                    <SingleFormInputSkeleton />
                    <SingleFormInputSkeleton />
                    <SingleFormInputSkeleton />
                  </>
                ) : (patientInputRut || patientInputPassport) && showAdditionalFields ? (
                  <>
                    <SingleFormInput
                      title="Nombre"
                      parameterName="name"
                      errorText="Debes escribir el nombre del paciente."
                      register={register}
                      errors={errors}
                      onBlur={() =>
                        capitalizeParametValue("name", getValues, setValue, trigger)
                      }
                      obscureInTracker={true}
                    />

                    <SingleFormInput
                      title="Apellido paterno"
                      parameterName="firstSurname"
                      errorText="Debes escribir el apellido paterno del paciente."
                      register={register}
                      errors={errors}
                      onBlur={() =>
                        capitalizeParametValue(
                          "firstSurname",
                          getValues,
                          setValue,
                          trigger,
                        )
                      }
                      obscureInTracker={true}
                    />

                    <SingleFormInput
                      title="Apellido materno"
                      parameterName="secondSurname"
                      errorText="Debes escribir el apellido materno del paciente."
                      register={register}
                      errors={errors}
                      autoComplete="off"
                      onBlur={() =>
                        capitalizeParametValue(
                          "secondSurname",
                          getValues,
                          setValue,
                          trigger,
                        )
                      }
                      obscureInTracker={true}
                    />

                    <BirthdateInput
                      control={control}
                      errorManagement={{ errors }}
                      obscureInTracker={true}
                    />

                    {birthdateDetails.weightNeeded && (
                      <SingleFormInput
                        title="Peso"
                        parameterName="weight"
                        inputType="number"
                        numericInputType="decimal"
                        numericMaxValue={100}
                        numericExternalValue={weight}
                        handleNumericExternalValueChange={(value) =>
                          handleNumericInputChange(
                            "weight",
                            String(value),
                            clearErrors,
                            setValue,
                          )
                        }
                        units="kg"
                        errorText="Debes ingresar el peso del paciente."
                        register={register}
                        errors={errors}
                        validate={(value: string) =>
                          validateWeight(value) || "Debes ingresar un peso menor a 100 kg"
                        }
                        obscureInTracker={true}
                      />
                    )}

                    <ExtraWhiteSpace />

                    <BottomActionArea>
                      <BottomActionButton
                        onClick={handleSubmit(onPatientDataSubmit)}
                        disabled={!patientInputRut && !patientInputPassport}
                      >
                        Siguiente
                      </BottomActionButton>
                    </BottomActionArea>
                  </>
                ) : null}
                {!showAdditionalFields && (
                  <>
                    {!loadingPatientData && (
                      <Column>
                        {!loadingPatientData && (
                          <>
                            <FormControl fullWidth>
                              <DocumentTypeSelection
                                value={selectedDocumentType}
                                onChange={handleDocumentTypeChange}
                                disabled={showAdditionalFields || loadingPatientData}
                              />
                            </FormControl>

                            <FormControl fullWidth>
                              <InputLabel
                                disabled={showAdditionalFields || loadingPatientData}
                              >
                                Número de Documento
                              </InputLabel>
                              <ObscuredSingleInput
                                placeholder={
                                  selectedDocumentType === DocumentType.RUT
                                    ? "12.345.678-9"
                                    : "ABC123456789"
                                }
                                {...(selectedDocumentType === DocumentType.RUT
                                  ? register("rut", {
                                      required: "Debes escribir el RUT del paciente.",
                                    })
                                  : register("passport", {
                                      required:
                                        "Debes escribir el Pasaporte del paciente.",
                                    }))}
                                onBlur={handleDocumentNumberBlur}
                                onKeyDown={handleDocumentNumberKeyDown}
                                onChange={handleDocumentNumberOnChange}
                                disabled={showAdditionalFields || loadingPatientData}
                                error={
                                  selectedDocumentType === DocumentType.RUT
                                    ? showRutError || !!errors["rut"]
                                    : showPassportError || !!errors["passport"]
                                }
                                inputProps={{
                                  "data-openreplay-obscured": "",
                                }}
                              />

                              {selectedDocumentType === DocumentType.RUT && (
                                <>
                                  {showRutError && !errors.rut && (
                                    <ErrorMessage>RUT no válido</ErrorMessage>
                                  )}
                                  {!showRutError && errors.rut && (
                                    <ErrorMessage>{errors.rut.message}</ErrorMessage>
                                  )}
                                </>
                              )}

                              {selectedDocumentType === DocumentType.PASSPORT && (
                                <>
                                  {showPassportError && !errors.passport && (
                                    <ErrorMessage>Pasaporte no válido</ErrorMessage>
                                  )}
                                  {!showPassportError && errors.passport && (
                                    <ErrorMessage>{errors.passport.message}</ErrorMessage>
                                  )}
                                </>
                              )}
                            </FormControl>
                          </>
                        )}
                        <ShareLink />
                      </Column>
                    )}

                    <BottomActionArea>
                      <BottomActionButton
                        onClick={handleSubmit(handleOnSubmitDocumentNumber)}
                        disabled={!hasValidDocument || loadingPatientData}
                      >
                        Siguiente
                      </BottomActionButton>
                    </BottomActionArea>
                  </>
                )}
              </>
            ) : (
              <>
                <SingleFormInput
                  title="Correo electrónico"
                  parameterName="email"
                  errorText="Debes escribir el correo electrónico del paciente."
                  // description="A este correo notificaremos al paciente."
                  register={register}
                  errors={errors}
                  inputType="email"
                  onBlur={() => {
                    const emailValue = getValues("email");

                    if (emailValue) {
                      setValue("email", emailValue.toLowerCase());
                    }

                    trigger("email");
                  }}
                  validate={(value) => {
                    const emailRegex =
                      /^([a-zA-Z0-9_\-+.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/;

                    if (emailRegex.exec(value)) {
                      return true;
                    } else {
                      return "El correo electrónico está incompleto";
                    }
                  }}
                  obscureInTracker={true}
                />

                {phoneInputRequired && (
                  <PhoneInput
                    register={register}
                    errors={errors}
                    control={control}
                    setValue={setValue}
                    trigger={trigger}
                    clearErrors={clearErrors}
                    obscureInTracker={true}
                    phoneCodeState={{
                      currentPhoneCode,
                      setCurrentPhoneCode,
                    }}
                  />
                )}

                <InputFieldWrapper label="Región" error={errors.region?.message}>
                  <Controller
                    name={"region"}
                    control={control}
                    defaultValue="Metropolitana de Santiago"
                    rules={{
                      required: "Debes seleccionar la región del paciente.",
                    }}
                    render={({ ...props }) => (
                      <Autocomplete
                        disablePortal
                        value={selectedRegion}
                        options={regions}
                        getOptionLabel={(region) => region}
                        renderInput={(params) => (
                          <WhiteTextField
                            error={Boolean(errors.region)}
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              "data-openreplay-obscured": "",
                            }}
                          />
                        )}
                        onChange={(_, data) => {
                          setValue("commune", "");
                          if (data) clearErrors("region");
                          props.field.onChange(data);
                        }}
                      />
                    )}
                  />
                </InputFieldWrapper>

                <InputFieldWrapper label="Comuna" error={errors.commune?.message}>
                  <Controller
                    name={"commune"}
                    control={control}
                    rules={{
                      required: "Debes seleccionar la comuna del paciente.",
                    }}
                    render={({ ...props }) => (
                      <Autocomplete
                        disablePortal
                        value={selectedCommune}
                        options={communes}
                        getOptionLabel={(commune) => commune}
                        renderInput={(params) => (
                          <WhiteTextField
                            error={Boolean(errors.commune)}
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              "data-openreplay-obscured": "",
                            }}
                          />
                        )}
                        onChange={(_, data) => {
                          if (data) clearErrors("commune");
                          props.field.onChange(data);
                        }}
                      />
                    )}
                  />
                </InputFieldWrapper>

                <SingleFormInput
                  title="Dirección"
                  parameterName="street"
                  errorText="Debes escribir la dirección del paciente."
                  register={register}
                  errors={errors}
                  obscureInTracker={true}
                />

                <ExtraWhiteSpace />

                <BottomActionArea>
                  <BottomActionButton type="submit">Siguiente</BottomActionButton>
                </BottomActionArea>
              </>
            )}
          </FormContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
}
